import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function genSessionId() {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export default function Chat() {
  const { t } = useLanguage();
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem("civic.chat.session");
    if (existing) return existing;
    const s = genSessionId();
    sessionStorage.setItem("civic.chat.session", s);
    return s;
  });

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Namaste! I'm Sanskaar, your guide to civic etiquette abroad. Ask me anything — tipping rules, dress codes, do's and don'ts, laws you should know. I'll keep answers practical and short.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chat/send`, { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I had trouble responding. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="chat-page" className="fade-in max-w-4xl mx-auto px-6 lg:px-12 py-12">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-sm bg-[#002FA7] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">
              {t.chat.title}
            </h1>
            <p className="text-sm text-[#475569]">{t.chat.sub}</p>
          </div>
        </div>
      </div>

      {/* Suggestions */}
      {messages.length <= 1 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {t.chat.suggestions.map((s, i) => (
            <button
              key={i}
              data-testid={`chat-suggestion-${i}`}
              onClick={() => send(s)}
              className="text-sm border border-[#E2E8F0] bg-white hover:border-[#002FA7] hover:text-[#002FA7] transition-all rounded-sm px-3 py-1.5"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div data-testid="chat-messages" className="space-y-5 mb-8 min-h-[300px]">
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`chat-message-${m.role}-${i}`}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-sm p-4 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#002FA7] text-white"
                  : "bg-white border border-[#E2E8F0] text-[#0F172A]"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div data-testid="chat-loading" className="flex justify-start">
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-4 text-sm text-[#475569] flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-[#002FA7] rounded-full animate-pulse" />
              {t.chat.thinking}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="sticky bottom-4">
        <div className="flex gap-2 bg-white border border-[#E2E8F0] rounded-sm p-2 shadow-sm">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={t.chat.placeholder}
            className="flex-1 px-3 py-2 outline-none bg-transparent text-sm"
          />
          <button
            data-testid="chat-send-button"
            onClick={() => send()}
            disabled={loading || !input.trim()}
            className={`btn-primary !py-2 ${loading || !input.trim() ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">{t.chat.send}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
